import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined'
import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import FilterIcon from 'assets/FilterIcon'
import { ReactComponent as ArrowNext } from 'assets/fill_back_arrow.svg'
import { ReactComponent as VideoIcon } from 'assets/translation_deviations_logo.svg'
import { getFatnessColorByTable } from 'components/cattles/fatnessPage/shared/fatnessScoreColor'
import { getYearsOld, morphForWords } from 'components/shared/processes'
import { ColumnTypes, LameHeadsColumnKey } from 'models'
import { ICowsList } from 'models/ICattlesData'
import moment from 'moment'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import SelectSmall from 'ui/menu/SelectForm'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'
import {
	getLamenessVideoMockMode,
	isLamenessVideoMockEnabled,
	LAMENESS_PHOTO_MOCK_URL,
	LAMENESS_VIDEO_MOCK_URL,
} from './helper/lamenessVideoMock'
import { LAME_HEADS_SCORE_OPTIONS } from './helper/lameHeadsScoreOptions'
import LamenessVideoModal from './LamenessVideoModal'

export type LameHeadsSortOrder =
	| 'limpness_start_dt'
	| 'limpness_change_dt'
	| '-limpness_change_dt'
	| null

interface LameHeadsTableProps {
	cattlesInfo: ICowsList[]
	fetchingCowsList: boolean
	sortOrder: LameHeadsSortOrder
	onSortOrder: (order: Exclude<LameHeadsSortOrder, null>) => void
	scoreFilter: number[]
	setScoreFilter: (value: number[] | ((prev: number[]) => number[])) => void
	getColumn: (key: LameHeadsColumnKey) => ColumnTypes | undefined
	page: number
	loadedPage: number
	pageSize: number
	totalCount: number
	onPageChange: (page: number) => void
	hasError: boolean
	showFilteredEmptyState: boolean
}

interface SortableHeaderProps {
	title: string
	order: Exclude<LameHeadsSortOrder, null>
	sortOrder: LameHeadsSortOrder
	onSortOrder: LameHeadsTableProps['onSortOrder']
}

const SortableHeader = ({
	title,
	order,
	sortOrder,
	onSortOrder,
}: SortableHeaderProps) => (
	<Stack direction='row' alignItems='center' gap='4px'>
		<TypographySecondary>{title}</TypographySecondary>
		<FilterIcon
			onClick={() => onSortOrder(order)}
			color={sortOrder === order ? COLORS.main : undefined}
		/>
	</Stack>
)

const HeaderCell = ({
	children,
	width,
	sx,
}: {
	children: ReactNode
	width?: string
	sx?: object
}) => (
	<TableCell
		align='left'
		padding='none'
		sx={{ minWidth: width, backgroundColor: '#fff', ...sx }}
	>
		{children}
	</TableCell>
)

const bodyCellSx = {
	verticalAlign: 'top',
	padding: '8px 0px',
}

const stickyTagSx = {
	minWidth: '160px',
	width: '16%',
	position: 'sticky',
	left: 0,
	background: '#fff',
	zIndex: 2,
	paddingRight: '16px',
} as const

const stickyActionsSx = {
	minWidth: '113px',
	width: '113px',
	position: 'sticky',
	right: 0,
	background: '#fff',
	zIndex: 2,
} as const

const LameHeadsTable = ({
	cattlesInfo,
	fetchingCowsList,
	sortOrder,
	onSortOrder,
	scoreFilter,
	setScoreFilter,
	getColumn,
	page,
	loadedPage,
	pageSize,
	totalCount,
	onPageChange,
	hasError,
	showFilteredEmptyState,
}: LameHeadsTableProps) => {
	const [selectedCow, setSelectedCow] = useState<ICowsList | null>(null)
	const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
	const [pendingNavigation, setPendingNavigation] = useState<
		'prev' | 'next' | null
	>(null)
	const [navigationOriginPage, setNavigationOriginPage] = useState<
		number | null
	>(null)
	const [manualScores, setManualScores] = useState<
		Record<
			number,
			{ systemScore: number; manualScore: number; editedBy?: string }
		>
	>({})
	const { userData } = useAppSelector(state => state.userReducer)
	const videoMockEnabled = isLamenessVideoMockEnabled()
	const videoMockMode = getLamenessVideoMockMode()
	const containerRef = useRef<HTMLDivElement | null>(null)
	const hasVideo = useCallback(
		(cow: ICowsList) => videoMockEnabled || Boolean(cow.limpness_video_url),
		[videoMockEnabled],
	)
	const findVideoIndex = useCallback(
		(startIndex: number, step: 1 | -1) => {
			for (
				let index = startIndex;
				index >= 0 && index < cattlesInfo.length;
				index += step
			) {
				if (hasVideo(cattlesInfo[index])) return index
			}

			return null
		},
		[cattlesInfo, hasVideo],
	)

	useEffect(() => {
		containerRef.current?.scrollTo({ top: 0, behavior: 'auto' })
	}, [cattlesInfo, page])

	const openCowPage = (tagId: number | string) => {
		const url = `/cattles/cow/${encodeURIComponent(String(tagId))}`
		window.open(url, '_blank', 'noopener,noreferrer')
	}

	const prepareCowForModal = useCallback(
		(cow: ICowsList) => {
			const videoCow = videoMockMode
				? {
						...cow,
						limpness_video_url: LAMENESS_VIDEO_MOCK_URL,
						limpness_side_photo_url:
							videoMockMode === '1'
								? LAMENESS_PHOTO_MOCK_URL
								: cow.limpness_side_photo_url,
						limpness_change_dt: cow.limpness_change_dt ?? Date.now(),
						last_limpness_tracking_dt:
							videoMockMode === '2'
								? moment().subtract(8, 'days').valueOf()
								: Date.now(),
					}
				: cow
			const localScore = manualScores[cow.id]

			return localScore
				? {
						...videoCow,
						limpness: localScore.manualScore,
						limpness_score: localScore.systemScore,
						limpness_score_is_false: true,
						limpness_score_manual: localScore.manualScore,
						limpness_score_manual_by:
							videoCow.limpness_score_manual_by || localScore.editedBy || null,
					}
				: videoCow
		},
		[manualScores, videoMockMode],
	)

	const openVideoModal = (cow: ICowsList, index: number) => {
		setSelectedIndex(index)
		setSelectedCow(prepareCowForModal(cow))
	}

	const hasPreviousPage = page > 1
	const hasNextPage = page * pageSize < totalCount
	const previousVideoIndex =
		selectedIndex == null ? null : findVideoIndex(selectedIndex - 1, -1)
	const nextVideoIndex =
		selectedIndex == null ? null : findVideoIndex(selectedIndex + 1, 1)

	const showPreviousVideo = () => {
		if (selectedIndex == null || pendingNavigation) return

		if (previousVideoIndex != null) {
			setSelectedIndex(previousVideoIndex)
			setSelectedCow(prepareCowForModal(cattlesInfo[previousVideoIndex]))
			return
		}

		if (hasPreviousPage) {
			setNavigationOriginPage(page)
			setPendingNavigation('prev')
			onPageChange(page - 1)
		}
	}

	const showNextVideo = () => {
		if (selectedIndex == null || pendingNavigation) return

		if (nextVideoIndex != null) {
			setSelectedIndex(nextVideoIndex)
			setSelectedCow(prepareCowForModal(cattlesInfo[nextVideoIndex]))
			return
		}

		if (hasNextPage) {
			setNavigationOriginPage(page)
			setPendingNavigation('next')
			onPageChange(page + 1)
		}
	}

	useEffect(() => {
		if (
			!pendingNavigation ||
			fetchingCowsList ||
			loadedPage !== page ||
			cattlesInfo.length === 0
		)
			return

		const targetIndex =
			pendingNavigation === 'next'
				? findVideoIndex(0, 1)
				: findVideoIndex(cattlesInfo.length - 1, -1)

		if (targetIndex != null) {
			setSelectedIndex(targetIndex)
			setSelectedCow(prepareCowForModal(cattlesInfo[targetIndex]))
			setPendingNavigation(null)
			setNavigationOriginPage(null)
			return
		}

		const canContinue =
			pendingNavigation === 'next' ? hasNextPage : hasPreviousPage

		if (canContinue) {
			onPageChange(page + (pendingNavigation === 'next' ? 1 : -1))
			return
		}

		setPendingNavigation(null)
		if (navigationOriginPage != null && navigationOriginPage !== page) {
			onPageChange(navigationOriginPage)
		}
		setNavigationOriginPage(null)
	}, [
		cattlesInfo,
		findVideoIndex,
		fetchingCowsList,
		hasNextPage,
		hasPreviousPage,
		loadedPage,
		navigationOriginPage,
		onPageChange,
		page,
		pendingNavigation,
		prepareCowForModal,
	])

	const canShowPrevious =
		selectedIndex != null &&
		!pendingNavigation &&
		(previousVideoIndex != null || hasPreviousPage)
	const canShowNext =
		selectedIndex != null &&
		!pendingNavigation &&
		(nextVideoIndex != null || hasNextPage)
	const selectedCowChangedInSession = selectedCow
		? Boolean(manualScores[selectedCow.id])
		: false

	const closeVideoModal = () => {
		setSelectedCow(null)
		setSelectedIndex(null)
		setPendingNavigation(null)
		setNavigationOriginPage(null)
	}

	const handleScoreUpdated = (cowId: number, score: number) => {
		const sourceCow =
			selectedCow?.id === cowId
				? selectedCow
				: cattlesInfo.find(cow => cow.id === cowId)

		if (!sourceCow) return

		const editedBy =
			[userData?.firstname, userData?.lastname].filter(Boolean).join(' ') ||
			userData?.username ||
			sourceCow.limpness_score_manual_by ||
			undefined

		setManualScores(currentScores => {
			const systemScore =
				currentScores[cowId]?.systemScore ??
				sourceCow.limpness_score ??
				sourceCow.limpness

			return {
				...currentScores,
				[cowId]: { systemScore, manualScore: score, editedBy },
			}
		})
		setSelectedCow(currentCow =>
			currentCow?.id === cowId
				? {
						...currentCow,
						limpness: score,
						limpness_score: currentCow.limpness_score ?? currentCow.limpness,
						limpness_score_is_false: true,
						limpness_score_manual: score,
						limpness_score_manual_by:
							editedBy || currentCow.limpness_score_manual_by,
					}
				: currentCow,
		)
	}

	const visibleMiddleColumns = [
		getColumn(LameHeadsColumnKey.GROUP)?.isShowColumn,
		getColumn(LameHeadsColumnKey.LIMPNESS)?.isShowColumn,
		getColumn(LameHeadsColumnKey.LIMPNESS_LENGTH)?.isShowColumn,
		getColumn(LameHeadsColumnKey.LIMPNESS_CHANGE_DT)?.isShowColumn,
		getColumn(LameHeadsColumnKey.COW_AGE)?.isShowColumn,
		getColumn(LameHeadsColumnKey.LACTATION_DAY)?.isShowColumn,
		getColumn(LameHeadsColumnKey.FATNESS)?.isShowColumn,
	].filter(Boolean).length

	const colSpan = 3 + visibleMiddleColumns

	return (
		<>
			<TableContainer
				ref={containerRef}
				sx={{
					marginTop: '4px',
					overflowY: 'auto',
					overflowX: 'auto',
					width: '100%',
					maxWidth: '100%',
					minWidth: 0,
					height: 'calc(100vh - 350px)',
					'&::-webkit-scrollbar': {
						width: 4,
						height: 4,
					},
					'&::-webkit-scrollbar-track': {
						backgroundColor: '#DBDBDB',
						borderRadius: 12,
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: COLORS.icon,
						borderRadius: 12,
					},
				}}
			>
				<Table stickyHeader sx={{ width: '100%', minWidth: '900px' }}>
					<TableHead
						sx={{
							[`& .${tableCellClasses.root}`]: {
								backgroundColor: '#fff',
							},
						}}
					>
						<TableRow sx={{ width: '100%', height: '50px' }}>
							<HeaderCell
								width='160px'
								sx={{ ...stickyTagSx, top: 0, zIndex: 5 }}
							>
								<TypographySecondary>
									Номер бирки
									<br />
									Номер ошейника
								</TypographySecondary>
							</HeaderCell>
							{getColumn(LameHeadsColumnKey.GROUP)?.isShowColumn && (
								<HeaderCell width='160px'>
									<TypographySecondary>Группа</TypographySecondary>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.LIMPNESS)?.isShowColumn && (
								<HeaderCell width='160px'>
									<SelectSmall
										typeArray={LAME_HEADS_SCORE_OPTIONS}
										isCheckbox
										isMultiplaySelect
										showSelectedCount
										state={setScoreFilter}
										selectedArray={scoreFilter}
										placeholder='Хромота'
										isClearIcon
										floatingLabel='Хромота'
										height='40px'
										width='134px'
									/>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.LIMPNESS_LENGTH)?.isShowColumn && (
								<HeaderCell width='160px'>
									<SortableHeader
										title='Продолжительность'
										order='limpness_start_dt'
										sortOrder={sortOrder}
										onSortOrder={onSortOrder}
									/>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.LIMPNESS_CHANGE_DT)
								?.isShowColumn && (
								<HeaderCell width='146px'>
									<SortableHeader
										title='Последний замер'
										order='-limpness_change_dt'
										sortOrder={sortOrder}
										onSortOrder={onSortOrder}
									/>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.COW_AGE)?.isShowColumn && (
								<HeaderCell width='146px'>
									<TypographySecondary>Возраст коровы</TypographySecondary>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.LACTATION_DAY)?.isShowColumn && (
								<HeaderCell width='100px'>
									<TypographySecondary>
										День
										<br />
										лактации
									</TypographySecondary>
								</HeaderCell>
							)}
							{getColumn(LameHeadsColumnKey.FATNESS)?.isShowColumn && (
								<HeaderCell width='146px'>
									<TypographySecondary>Упитанность, балл</TypographySecondary>
								</HeaderCell>
							)}
							<TableCell
								align='left'
								padding='none'
								sx={{ ...stickyActionsSx, top: 0, zIndex: 5 }}
							>
								<Box pl='16px'>
									<TypographySecondary>Действия</TypographySecondary>
								</Box>
							</TableCell>
						</TableRow>
					</TableHead>
					{fetchingCowsList ? (
						<TableBody>
							<TableRow>
								<TableCell colSpan={colSpan} align='center' padding='none'>
									<TableSkeleton
										widthForBody='100%'
										heightForBody='34px'
										rowCount={36}
										totalHeight='calc(100vh - 350px)'
									/>
								</TableCell>
							</TableRow>
						</TableBody>
					) : hasError || cattlesInfo.length === 0 ? (
						<TableBody>
							<TableRow
								sx={{
									width: '100%',
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
										height: '400px',
									},
								}}
							>
								<TableCell colSpan={colSpan} align='center' padding='none'>
									<Stack
										direction='column'
										justifyContent='center'
										alignItems='center'
										gap='8px'
									>
										{hasError || showFilteredEmptyState ? (
											<>
												<TypographySecondaryBold16>
													Нет данных
												</TypographySecondaryBold16>
												<TypographySecondary14>
													Нет данных, соответствующих выбранным условиям.
												</TypographySecondary14>
											</>
										) : (
											<TypographySecondary14>
												Хромых голов не обнаружено
											</TypographySecondary14>
										)}
									</Stack>
								</TableCell>
							</TableRow>
						</TableBody>
					) : (
						<TableBody>
							{cattlesInfo.map((cow, index) => {
								const localScore = manualScores[cow.id]
								const score =
									localScore?.manualScore ??
									(cow.limpness_score_is_false
										? (cow.limpness_score_manual ?? cow.limpness)
										: cow.limpness)

								return (
									<TableRow
										key={cow.id}
										sx={{
											height: '46px',
											[`& .${tableCellClasses.root}`]: {
												borderBottom: 'none',
											},
										}}
									>
										<TableCell
											align='left'
											padding='none'
											sx={{ ...bodyCellSx, ...stickyTagSx }}
										>
											<Box display='flex' alignItems='center' gap='4px'>
												<Box display='flex' flexDirection='column'>
													<TypographyPrimary>
														{cow.tag_id || '-'}
													</TypographyPrimary>
													{cow.collar_num && (
														<TypographySecondary14>
															{cow.collar_num}
														</TypographySecondary14>
													)}
												</Box>
												{(cow.limpness_score_is_false || localScore) && (
													<TooltipHover
														title='Балл хромоты был изменен вручную'
														placement='top-start'
														oneLine
														disableHoverBackground
													>
														<Box
															sx={{
																display: 'flex',
																color: COLORS.icon,
																cursor: 'pointer',
																'&:hover': { color: COLORS.main },
															}}
														>
															<ReportGmailerrorredOutlinedIcon
																sx={{ fontSize: 20, color: 'inherit' }}
															/>
														</Box>
													</TooltipHover>
												)}
											</Box>
										</TableCell>
										{getColumn(LameHeadsColumnKey.GROUP)?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{
													...bodyCellSx,
													minWidth: '160px',
													width: '160px',
												}}
											>
												<TypographyPrimary>
													{cow.group?.title || '-'}
												</TypographyPrimary>
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.LIMPNESS)?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '160px' }}
											>
												{score ? (
													<TypographyPrimary>
														{score}{' '}
														<span
															style={{
																fontSize: '12px',
																color: COLORS.secondary,
															}}
														>
															{morphForWords(score, [
																'балл',
																'балла',
																'баллов',
															])}
														</span>
													</TypographyPrimary>
												) : (
													<TypographyPrimary>-</TypographyPrimary>
												)}
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.LIMPNESS_LENGTH)
											?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '160px' }}
											>
												<TypographyPrimary>
													{cow.limpness_length || '-'}
												</TypographyPrimary>
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.LIMPNESS_CHANGE_DT)
											?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '146px' }}
											>
												<TypographyPrimary>
													{cow.limpness_change_dt
														? moment(cow.limpness_change_dt).format('DD.MM.YY')
														: '-'}
												</TypographyPrimary>
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.COW_AGE)?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '146px' }}
											>
												<TypographyPrimary>
													{cow.birth_date ? getYearsOld(cow.birth_date) : '-'}
												</TypographyPrimary>
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.LACTATION_DAY)
											?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '100px' }}
											>
												<TypographySecondary14>
													{cow.lactation_length != null
														? `${cow.lactation_length} ${morphForWords(
																cow.lactation_length,
																['день', 'дня', 'дней'],
															)}`
														: '-'}
												</TypographySecondary14>
											</TableCell>
										)}
										{getColumn(LameHeadsColumnKey.FATNESS)?.isShowColumn && (
											<TableCell
												align='left'
												padding='none'
												sx={{ ...bodyCellSx, minWidth: '146px' }}
											>
												{cow.fatness != null ? (
													<TypographyPrimary
														sx={{
															color: getFatnessColorByTable(
																cow.fatness,
																cow.lactation_length,
																cow.fatness_status,
															),
														}}
													>
														{cow.fatness}{' '}
														<span style={{ fontSize: '12px' }}>
															{morphForWords(cow.fatness, [
																'балл',
																'балла',
																'баллов',
															])}
														</span>
													</TypographyPrimary>
												) : (
													<TypographyPrimary>-</TypographyPrimary>
												)}
											</TableCell>
										)}
										<TableCell
											align='left'
											padding='none'
											sx={{ ...bodyCellSx, ...stickyActionsSx }}
										>
											<Box
												display='flex'
												flexDirection='row'
												alignItems='center'
												justifyContent='flex-start'
												gap='8px'
												pl='12px'
											>
												<Box width='32px' height='32px'>
													{(cow.limpness_video_url || videoMockEnabled) && (
														<TooltipHover
															title='Смотреть видеофиксацию хромоты'
															placement='bottom-end'
															oneLine
														>
															<VideoIcon
																onClick={() => openVideoModal(cow, index)}
																style={{ cursor: 'pointer' }}
															/>
														</TooltipHover>
													)}
												</Box>
												<TooltipHover
													title='Перейти на страницу коровы'
													placement='bottom-start'
													oneLine
												>
													<ArrowNext
														onClick={() => openCowPage(cow.tag_id)}
														style={{
															cursor: 'pointer',
															transform: 'rotate(180deg)',
														}}
														stroke={COLORS.secondary}
													/>
												</TooltipHover>
											</Box>
										</TableCell>
									</TableRow>
								)
							})}
						</TableBody>
					)}
				</Table>
			</TableContainer>
			<LamenessVideoModal
				open={Boolean(selectedCow)}
				cow={selectedCow}
				onClose={closeVideoModal}
				onScoreUpdated={handleScoreUpdated}
				canPrev={canShowPrevious}
				canNext={canShowNext}
				onPrev={showPreviousVideo}
				onNext={showNextVideo}
				isLoading={Boolean(pendingNavigation)}
				changedInSession={selectedCowChangedInSession}
			/>
		</>
	)
}

export default LameHeadsTable
